import { useSelectedRowSnapshotsStore } from "@/components/query-table/stores/selectedRowSnapshotsStore";
import { useSelectedRowIds } from "@/components/query-table/hooks/useSelectedRowIds";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useSelectionStaleness } from "@/modules/contractorsPlannedHoursWizard/hooks/useSelectionStaleness";
import { ContractorsPlannedHoursWizardService } from "@/services/contractorsPlannedHoursWizardService";
import { ApiError } from "@/services/api";
import {
  CalculateContractorsPlannedHoursWizardRequest,
  DisplayContractorsPlannedHoursWizardAllocation,
  ReadContractorsPlannedHoursWizardResult,
} from "@/types/contractorsPlannedHoursWizard";
import { useCallback, useState } from "react";

export type ContractorsPlannedHoursWizardTabId = "selecting" | "results";

export function useContractorsPlannedHoursWizardTabs() {
  const [calculationResults, setCalculationResults] = useState<
    ReadContractorsPlannedHoursWizardResult[]
  >([]);
  const [calculationSnapshot, setCalculationSnapshot] = useState<
    DisplayContractorsPlannedHoursWizardAllocation[]
  >([]);
  const [activeTab, setActiveTab] =
    useState<ContractorsPlannedHoursWizardTabId>("selecting");

  const selectedIds = useSelectedRowIds({});
  const {
    markSelectionRequested,
    markSelectionCalculated,
    hasUnappliedSelectionChanges,
  } = useSelectionStaleness(selectedIds);

  const calculateMutation = useMutationWithErrorHandling<
    ReadContractorsPlannedHoursWizardResult[],
    ApiError,
    CalculateContractorsPlannedHoursWizardRequest,
    unknown
  >({
    mutationFn: ContractorsPlannedHoursWizardService.calculate,
    onSuccess: (data) => {
      setCalculationResults(data);
      markSelectionCalculated();
      setActiveTab("results");
    },
  });

  const handleCalculate = useCallback(() => {
    const uniqueIds = [...new Set(selectedIds)];
    if (uniqueIds.length === 0) {
      return;
    }
    const rows = useSelectedRowSnapshotsStore
      .getState()
      .getSnapshotRowsForIds(
        "contractorsPlannedHoursWizardAllocations",
        uniqueIds,
      ) as DisplayContractorsPlannedHoursWizardAllocation[];
    setCalculationSnapshot(rows.map((r) => ({ ...r })));
    markSelectionRequested(uniqueIds);
    calculateMutation.mutate({ schedules: uniqueIds });
  }, [selectedIds, calculateMutation, markSelectionRequested]);

  const calculateDisabled =
    selectedIds.size === 0 || calculateMutation.isPending;

  return {
    activeTab,
    setActiveTab,
    calculationResults,
    calculationSnapshot,
    calculateMutation,
    handleCalculate,
    calculateDisabled,
    hasUnappliedSelectionChanges,
  };
}
